import React, { useState, useMemo } from 'react';
import { keepPreviousData } from '@tanstack/react-query';
import { useApiQuery } from 'hooks/useApiQuery';
import { GenericListScreenProps } from './types';
import BasicTable from 'ScreenLayouts/TableLayout/BasicTableLayout/BasicTable';
import DashboardLayout from 'ScreenLayouts/ContainerLayout/DashboardLayout';
import DashboardNavbar from 'ScreenLayouts/NavbarLayout/DashboardNavbar';
import Footer from 'ScreenFooters/DefaultFooter';
import MDBox from 'components/MDBox';
import MDTypography from 'components/MDTypography';
import Card from '@mui/material/Card';
import Icon from '@mui/material/Icon';

// Storage key prefix to persist page size per screen
const PAGE_SIZE_STORAGE_PREFIX = 'mediinstep_page_size_';

export function GenericListScreen<T = any>({
    title,
    apiName,
    columns,
    defaultPageSize = 10,
    dataTransform,
    actionComponent,
    extraParams,
    onRowClick,
    icon = "list"
}: GenericListScreenProps<T>) {
    // State
    // API uses 1-based page numbering
    const [pageNumber, setPageNumber] = useState(1);

    const [pageSize, setPageSize] = useState(() => {
        const saved = localStorage.getItem(`${PAGE_SIZE_STORAGE_PREFIX}${apiName}`);
        return saved ? parseInt(saved, 10) : defaultPageSize;
    });

    const [searchTerm, setSearchTerm] = useState<string>("");

    // API Query Parameters
    const queryParams = useMemo(() => ({
        PageNumber: pageNumber,
        PageSize: pageSize,
        SearchKeyword: searchTerm || undefined,
        ...(extraParams || {})
    }), [pageNumber, pageSize, searchTerm, extraParams]);

    // Fetch Data
    // TData defaults to any if not specified, but we explicit cast to avoid inference issues with keepPreviousData
    const { data, isLoading, isError, refetch } = useApiQuery<any>(apiName, queryParams, {
        placeholderData: keepPreviousData, // Keep UI stable while fetching new page
    });

    // Data Processing
    const { items, totalCount } = useMemo(() => {
        if (!data) return { items: [], totalCount: 0 };

        if (dataTransform) {
            return dataTransform(data);
        }

        // Default expectation based on LoginHistory: data IS the PagedList object
        // { items: [], totalCount: number, ... }
        return {
            items: data.items || [],
            totalCount: data.totalCount || 0
        };
    }, [data, dataTransform]);

    // Handlers

    // BasicTable calls goPage with 0-based index
    const handlePageChange = (pageIndex: number) => {
        setPageNumber(pageIndex + 1);
    };

    const handlePageSizeChange = (newPageSize: number) => {
        setPageSize(newPageSize);
        setPageNumber(1); // Reset to first page
        localStorage.setItem(`${PAGE_SIZE_STORAGE_PREFIX}${apiName}`, newPageSize.toString());
    };

    const handleSearch = (value: string) => {
        setSearchTerm(value);
        setPageNumber(1); // Reset to first page
    };

    // Pagination Criteria for BasicTable (Expects 0-based indexing)
    const paginationCriteria = useMemo(() => {
        const totalPages = Math.ceil(totalCount / pageSize);
        // BasicTable expects PageCount to be an array of indices [0, 1, 2, ...]
        const pageCountArray = Array.from({ length: Math.max(1, totalPages) }, (_, i) => i);
        const currentPageIndex = pageNumber - 1;
        const pageTenNumber = Math.floor(currentPageIndex / 10) * 10;

        return {
            PageNumber: currentPageIndex,
            PageCount: pageCountArray,
            PageTenNumber: pageTenNumber,
            totalCount: totalCount,
        };
    }, [totalCount, pageSize, pageNumber]);

    return (
        <DashboardLayout>
            <DashboardNavbar />
            <MDBox pt={6} pb={3} position="relative">
                <Card sx={{ "& table": { tableLayout: "fixed", width: "100%" } }}>
                    <MDBox p={3} lineHeight={1}>
                        <MDBox display="flex" justifyContent="space-between" alignItems="center">
                            <MDBox display="flex" alignContent="center">
                                <Icon fontSize="medium">{icon}</Icon>
                                <MDTypography ml={1} variant="h5" fontWeight="medium">
                                    {title}
                                </MDTypography>
                            </MDBox>
                            {actionComponent && <MDBox>{actionComponent}</MDBox>}
                        </MDBox>
                    </MDBox>

                    <BasicTable
                        table={{ columns, rows: items }}
                        showTotalEntries={true}
                        isSorted={false}
                        noEndBorder
                        entriesPerPage={{
                            defaultValue: pageSize,
                            entries: [10, 20, 50, 100],
                        }}
                        canSearch={true}
                        onSearch={handleSearch}
                        onRowClick={onRowClick}

                        // Server-side Pagination Props
                        pagination={paginationCriteria}
                        criteria={paginationCriteria}
                        goPage={handlePageChange}
                        storePageSize={pageSize}
                        setStorePageSize={handlePageSizeChange}
                    />
                </Card>
            </MDBox>
            <Footer />
        </DashboardLayout>
    );
}

export default GenericListScreen;

